"use client";

import React, { useState } from "react";
import { Badge } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import {
  Label,
  Input,
  Select,
  ErrorMessage,
} from "@/components/ui/form-elements";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/overlays";
import {
  createProjectAction,
  updateProjectAction,
  deleteProjectAction,
} from "@/actions/projects-actions";
import { useRouter } from "next/navigation";
import Link from "next/link";
import {
  Briefcase,
  Search,
  Plus,
  Euro,
  User,
  AlertTriangle,
  FolderGit,
  Compass,
  Pencil,
  Trash2,
} from "lucide-react";

interface ProjectItem {
  id: string;
  title: string;
  budget: number;
  revenue: number;
  status: "on_track" | "watch" | "at_risk" | "blocked";
  ownerName: string | null;
  ownerId: string | null;
}

interface AgentItem {
  id: string;
  name: string;
}

interface ProjectsClientProps {
  initialProjects: ProjectItem[];
  agents: AgentItem[];
}

export function ProjectsClient({
  initialProjects,
  agents,
}: ProjectsClientProps) {
  const router = useRouter();
  const [projectsList, setProjectsList] =
    useState<ProjectItem[]>(initialProjects);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Form State
  const [newTitle, setNewTitle] = useState("");
  const [newBudget, setNewBudget] = useState("");
  const [newRevenue, setNewRevenue] = useState("");
  const [newStatus, setNewStatus] = useState<
    "on_track" | "watch" | "at_risk" | "blocked"
  >("on_track");
  const [newOwnerId, setNewOwnerId] = useState("");

  // Edit Modal State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editProject, setEditProject] = useState<ProjectItem | null>(null);

  // Edit Form State
  const [editTitle, setEditTitle] = useState("");
  const [editBudget, setEditBudget] = useState("");
  const [editRevenue, setEditRevenue] = useState("");
  const [editStatus, setEditStatus] = useState<
    "on_track" | "watch" | "at_risk" | "blocked"
  >("on_track");
  const [editOwnerId, setEditOwnerId] = useState("");

  // Sync state with server props
  React.useEffect(() => {
    setProjectsList(initialProjects);
  }, [initialProjects]);

  const handleOpenModal = () => {
    setNewTitle("");
    setNewBudget("0");
    setNewRevenue("0");
    setNewStatus("on_track");
    setNewOwnerId("");
    setError("");
    setIsModalOpen(true);
  };

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!newTitle.trim()) {
      setError("Il titolo del progetto è obbligatorio.");
      return;
    }
    const budgetNum = parseFloat(newBudget);
    const revenueNum = parseFloat(newRevenue);

    if (isNaN(budgetNum) || budgetNum < 0) {
      setError("Il budget deve essere un numero valido maggiore o uguale a 0.");
      return;
    }
    if (isNaN(revenueNum) || revenueNum < 0) {
      setError("Il ricavo deve essere un numero valido maggiore o uguale a 0.");
      return;
    }

    setLoading(true);
    try {
      const res = await createProjectAction({
        title: newTitle,
        budget: budgetNum,
        revenue: revenueNum,
        status: newStatus,
        ownerId: newOwnerId || null,
      });

      if (res.error) {
        setError(res.error);
      } else {
        setIsModalOpen(false);
        router.refresh();
      }
    } catch (err: any) {
      setError(err.message || "Errore durante la creazione del progetto.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditModal = (project: ProjectItem) => {
    setEditProject(project);
    setEditTitle(project.title);
    setEditBudget(project.budget.toString());
    setEditRevenue(project.revenue.toString());
    setEditStatus(project.status);
    setEditOwnerId(project.ownerId || "");
    setError("");
    setIsEditModalOpen(true);
  };

  const handleEditProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editProject) return;
    setError("");

    if (!editTitle.trim()) {
      setError("Il titolo del progetto è obbligatorio.");
      return;
    }
    const budgetNum = parseFloat(editBudget);
    const revenueNum = parseFloat(editRevenue);

    if (isNaN(budgetNum) || budgetNum < 0) {
      setError("Il budget deve essere un numero valido maggiore o uguale a 0.");
      return;
    }
    if (isNaN(revenueNum) || revenueNum < 0) {
      setError("Il ricavo deve essere un numero valido maggiore o uguale a 0.");
      return;
    }

    setLoading(true);
    try {
      const res = await updateProjectAction({
        id: editProject.id,
        title: editTitle,
        budget: budgetNum,
        revenue: revenueNum,
        status: editStatus,
        ownerId: editOwnerId || null,
      });

      if (res.error) {
        setError(res.error);
      } else {
        setIsEditModalOpen(false);
        setEditProject(null);
        router.refresh();
      }
    } catch (err: any) {
      setError(err.message || "Errore durante la modifica del progetto.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProject = async (id: string) => {
    if (!confirm("Sei sicuro di voler eliminare questo progetto?")) return;
    setLoading(true);
    try {
      const res = await deleteProjectAction(id);
      if (res.error) {
        alert(res.error);
      } else {
        router.refresh();
      }
    } catch (err: any) {
      alert(err.message || "Errore durante l'eliminazione del progetto.");
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = projectsList.filter((project) => {
    const matchesSearch = project.title
      .toLowerCase()
      .includes(search.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || project.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: ProjectItem["status"]) => {
    switch (status) {
      case "on_track":
        return <Badge variant="success">On Track</Badge>;
      case "watch":
        return <Badge variant="warning">In Osservazione</Badge>;
      case "at_risk":
        return <Badge variant="error">A Rischio</Badge>;
      case "blocked":
        return <Badge variant="error">Bloccato</Badge>;
    }
  };

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(val);
  };

  return (
    <div className="space-y-6">
      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between bg-card p-4 rounded-xl border border-border">
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-text-muted" />
          <Input
            placeholder="Cerca progetti..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="flex w-full sm:w-auto gap-3 items-center justify-between sm:justify-end">
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-[180px]"
          >
            <option value="all">Tutti gli stati</option>
            <option value="on_track">In Corso (On Track)</option>
            <option value="watch">In Osservazione (Watch)</option>
            <option value="at_risk">A Rischio (At Risk)</option>
            <option value="blocked">Bloccato (Blocked)</option>
          </Select>

          <Button onClick={handleOpenModal} className="gap-1.5 shrink-0">
            <Plus className="h-4 w-4" />
            Nuovo Progetto
          </Button>
        </div>
      </div>

      {/* Projects Grid */}
      {filteredProjects.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-12 border border-dashed border-border rounded-2xl bg-card/50 text-center max-w-xl mx-auto my-8">
          <FolderGit className="h-10 w-10 text-primary-500 mb-3 animate-pulse" />
          <h3 className="text-base font-bold text-text-primary">
            Nessun progetto trovato
          </h3>
          <p className="text-sm text-text-secondary mt-1">
            Nessun progetto soddisfa i criteri di ricerca impostati. Crea un
            nuovo progetto per incominciare.
          </p>
          <Button onClick={handleOpenModal} className="mt-4 gap-1.5">
            <Plus className="h-4 w-4" />
            Crea Primo Progetto
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => (
            <Card
              key={project.id}
              className="relative overflow-hidden flex flex-col justify-between hover:border-primary-500/50 transition-all border-t-4 border-t-category-work cursor-pointer group hover:shadow-md"
              onClick={() => handleOpenEditModal(project)}
            >
              {/* Hover Actions */}
              <div className="absolute top-2.5 right-2.5 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all duration-200 transform translate-y-1 group-hover:translate-y-0 z-10">
                <Link
                  href={`/explore?nodeId=${project.id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="h-7 w-7 rounded-md bg-card border border-border flex items-center justify-center text-text-secondary hover:text-primary-600 hover:border-primary-500/30 hover:bg-primary-50/50 shadow-xs transition-all"
                  title="Esplora nel Grafo"
                >
                  <Compass className="h-3.5 w-3.5" />
                </Link>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenEditModal(project);
                  }}
                  className="h-7 w-7 rounded-md bg-card border border-border flex items-center justify-center text-text-secondary hover:text-primary-600 hover:border-primary-500/30 hover:bg-primary-50/50 shadow-xs transition-all cursor-pointer"
                  title="Modifica Progetto"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteProject(project.id);
                  }}
                  className="h-7 w-7 rounded-md bg-card border border-border flex items-center justify-center text-text-secondary hover:text-error hover:border-error/30 hover:bg-error-soft/50 shadow-xs transition-all cursor-pointer"
                  title="Elimina Progetto"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>

              <CardHeader className="pb-3 pr-24">
                <div className="flex justify-between items-start gap-3">
                  <div className="space-y-1 truncate">
                    <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">
                      Project Unit
                    </span>
                    <Link
                      href={`/work/projects/${project.id}`}
                      className="block truncate"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <CardTitle className="hover:text-primary-600 transition-colors text-base font-bold leading-tight truncate">
                        {project.title}
                      </CardTitle>
                    </Link>
                  </div>
                  <div className="shrink-0 group-hover:opacity-0 transition-opacity duration-150">
                    {getStatusBadge(project.status)}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 pb-4">
                {/* Financial Summary */}
                <div className="grid grid-cols-2 gap-4 bg-card-muted/40 p-3 rounded-lg border border-border/40 text-xs">
                  <div>
                    <span className="text-text-secondary font-medium">
                      Budget:
                    </span>
                    <p className="text-sm font-bold text-text-primary mt-0.5">
                      {formatCurrency(project.budget)}
                    </p>
                  </div>
                  <div>
                    <span className="text-text-secondary font-medium">
                      Fatturato:
                    </span>
                    <p className="text-sm font-bold text-success mt-0.5">
                      {formatCurrency(project.revenue)}
                    </p>
                  </div>
                </div>

                {/* Owner info */}
                <div className="flex items-center gap-1.5 text-xs text-text-secondary">
                  <User className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">
                    Proprietario:{" "}
                    {project.ownerId ? (
                      <Link
                        href={`/people/${project.ownerId}`}
                        className="hover:underline font-bold text-primary-600"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {project.ownerName}
                      </Link>
                    ) : (
                      <strong>Nessuno</strong>
                    )}
                  </span>
                </div>
              </CardContent>
              <CardFooter className="flex justify-end items-center bg-card-muted/20 px-6 py-3 border-t border-border/10 text-xs">
                <Link
                  href={`/work/projects/${project.id}`}
                  className="text-primary-600 hover:text-primary-700 font-semibold flex items-center gap-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  Vedi Dettagli &rarr;
                </Link>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      {/* Add Project Dialog */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuovo Progetto</DialogTitle>
            <DialogDescription>
              Crea un nodo di tipo Progetto per tracciare le milestone operative
              ed i dettagli finanziari.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateProject} className="space-y-4 my-2">
            <div className="space-y-1.5">
              <Label htmlFor="proj-title">Nome del Progetto</Label>
              <Input
                id="proj-title"
                placeholder="es. Integrazione API Stripe Checkout"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="proj-budget">Budget Allocato (€)</Label>
                <Input
                  id="proj-budget"
                  type="number"
                  placeholder="0"
                  value={newBudget}
                  onChange={(e) => setNewBudget(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="proj-revenue">Fatturato Previsto (€)</Label>
                <Input
                  id="proj-revenue"
                  type="number"
                  placeholder="0"
                  value={newRevenue}
                  onChange={(e) => setNewRevenue(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="proj-owner">Proprietario (Owner)</Label>
                <Select
                  id="proj-owner"
                  value={newOwnerId}
                  onChange={(e) => setNewOwnerId(e.target.value)}
                  disabled={loading}
                >
                  <option value="">Nessuno (Non assegnato)</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="proj-status">Stato Progetto</Label>
                <Select
                  id="proj-status"
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value as any)}
                  disabled={loading}
                >
                  <option value="on_track">In Corso (On Track)</option>
                  <option value="watch">In Osservazione</option>
                  <option value="at_risk">A Rischio</option>
                  <option value="blocked">Bloccato</option>
                </Select>
              </div>
            </div>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsModalOpen(false)}
                disabled={loading}
              >
                Annulla
              </Button>
              <Button type="submit" loading={loading}>
                Crea Progetto
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica Progetto</DialogTitle>
            <DialogDescription>
              Modifica i dettagli e lo stato del progetto operativo.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleEditProject} className="space-y-4 my-2">
            <div className="space-y-1.5">
              <Label htmlFor="edit-proj-title">Nome del Progetto</Label>
              <Input
                id="edit-proj-title"
                placeholder="es. Integrazione API Stripe Checkout"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="edit-proj-budget">Budget Allocato (€)</Label>
                <Input
                  id="edit-proj-budget"
                  type="number"
                  placeholder="0"
                  value={editBudget}
                  onChange={(e) => setEditBudget(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="edit-proj-revenue">
                  Fatturato Previsto (€)
                </Label>
                <Input
                  id="edit-proj-revenue"
                  type="number"
                  placeholder="0"
                  value={editRevenue}
                  onChange={(e) => setEditRevenue(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="edit-proj-owner">Proprietario (Owner)</Label>
                <Select
                  id="edit-proj-owner"
                  value={editOwnerId}
                  onChange={(e) => setEditOwnerId(e.target.value)}
                  disabled={loading}
                >
                  <option value="">Nessuno (Non assegnato)</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="edit-proj-status">Stato Progetto</Label>
                <Select
                  id="edit-proj-status"
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value as any)}
                  disabled={loading}
                >
                  <option value="on_track">In Corso (On Track)</option>
                  <option value="watch">In Osservazione</option>
                  <option value="at_risk">A Rischio</option>
                  <option value="blocked">Bloccato</option>
                </Select>
              </div>
            </div>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setIsEditModalOpen(false);
                  setEditProject(null);
                }}
                disabled={loading}
              >
                Annulla
              </Button>
              <Button type="submit" loading={loading}>
                Salva Modifiche
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
